/**
 * Assessments Hub — Public landing page that lists the 5 Nai Compliance Center pillars.
 *
 * Route: /assessments
 * Each pillar card links to its own assessment page (active) or shows "Em breve" (planned).
 */
import { Link } from 'react-router-dom'

interface PillarCard {
  id: string
  title: string
  tagline: string
  description: string
  icon: JSX.Element
  bullets: string[]
  duration: string
  domainCount: number
  questionCount: number
  href: string | null  // null = coming soon
  status: 'active' | 'coming_soon'
  comingSoonAt?: string
  accentFrom: string  // hex color
  accentTo: string    // hex color
  accentSolid: string // accent text/bullet color
}

const PILLARS: PillarCard[] = [
  {
    id: 'iga',
    title: 'IGA Maturity Assessment',
    tagline: 'Identity Governance & Administration',
    description:
      'Avalie a maturidade do seu programa de governança de identidades em 10 domínios — desde estratégia e ciclo de vida até PAM, terceiros e compliance.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <path d="m9 12 2 2 4-4"/>
      </svg>
    ),
    bullets: [
      'Estratégia, Governança e Ownership',
      'Ciclo de Vida e Provisionamento',
      'PAM, SoD e Certificação',
      'Terceiros, Compliance e Cultura',
    ],
    duration: '12 min',
    domainCount: 10,
    questionCount: 100,
    href: '/assessment/iga',
    status: 'active',
    accentFrom: '#06b6d4',
    accentTo: '#10b981',
    accentSolid: '#22d3ee',
  },
  {
    id: 'privacy',
    title: 'Privacy Maturity Assessment',
    tagline: 'Nai Data Privacy · LGPD · GDPR',
    description:
      'Diagnóstico de aderência à LGPD e GDPR — base legal, RIPD, gestão de incidentes, direitos do titular e tratamento de dados sensíveis.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
    bullets: [
      'Governança · DPO · ROPA · políticas',
      'Data Discovery (PII/PCI) + Data Map',
      'Direitos do titular · DSAR · SLA 15 dias',
      'Authoritative Repository + tokenização',
    ],
    duration: '15 min',
    domainCount: 5,
    questionCount: 23,
    href: '/assessment/privacy',
    status: 'active',
    accentFrom: '#8b5cf6',
    accentTo: '#d946ef',
    accentSolid: '#a78bfa',
  },
  {
    id: 'value_chain',
    title: 'Supplier Integrity Maturity Assessment',
    tagline: 'GRCWay · Integridade da Cadeia de Valor',
    description:
      'Mapeie o risco fiscal, trabalhista, ambiental e de direitos humanos da sua cadeia — fornecedores, coligadas, franqueados e clientes. Aderência ao Decreto 11.129/2022, NR-1 e NR-31, EUDR, trabalho forçado na UE e ao crédito de CBS de 2027.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
      </svg>
    ),
    bullets: [
      'Obrigações acessórias',
      'Due diligence de terceiros',
      'Aderência regulatória multi-norma',
      'Mapeamento de risco solidário',
    ],
    duration: '15 min',
    domainCount: 7,
    questionCount: 70,
    href: '/assessment/cadeia-de-valor',
    status: 'active',
    accentFrom: '#f59e0b',
    accentTo: '#f97316',
    accentSolid: '#fbbf24',
  },
  {
    id: 'sustainability',
    title: 'Sustentabilidade ESG',
    tagline: 'EVO ESG · GRI · IFRS S1/S2 · SASB',
    description:
      'Maturidade do programa ESG: GHG Protocol, GRI Standards, IFRS S2 (CVM 193/2023), SASB, ABNT PR 2030 — com integração IoT/telemetria.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 22 16 8"/>
        <path d="M2 22V11a9 9 0 0 1 18 0c0 4-2 7-6 10"/>
        <path d="M11 14a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
      </svg>
    ),
    bullets: [
      'GHG Protocol · 3 escopos',
      'GRI / IFRS S2 / SASB / TCFD',
      'CBAM · EUDR · SBCE · CVM 193',
      'IoT + Telemetria · auditabilidade',
    ],
    duration: '12 min',
    domainCount: 8,
    questionCount: 80,
    href: null,
    status: 'coming_soon',
    comingSoonAt: 'Q4/2026',
    accentFrom: '#84cc16',
    accentTo: '#22c55e',
    accentSolid: '#a3e635',
  },
  {
    id: 'cybersec',
    title: 'Cibersegurança · BS4IT',
    tagline: 'Mapeamento técnico para proposta personalizada',
    description:
      'Formulário em parceria com a BS4IT para coleta de servidores, ativos de SegInfo e informações complementares. Gera proposta personalizada de cibersegurança.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <path d="M9 11h6"/>
        <path d="M12 8v6"/>
      </svg>
    ),
    bullets: [
      'Dados cadastrais da empresa',
      'Cadastro de servidores e equipamentos',
      'Ativos de Segurança da Informação',
      '33 perguntas técnicas complementares',
    ],
    duration: '20 min',
    domainCount: 4,
    questionCount: 33,
    href: '/assessment/bs4it',
    status: 'active',
    accentFrom: '#ef4444',
    accentTo: '#f43f5e',
    accentSolid: '#fb7185',
  },
]

export default function AssessmentsHubPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050912] via-[#0a131f] to-[#050912] text-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#00284a]/85 border-b border-slate-800 px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <img
            src="https://nai-it.com.br/wp-content/uploads/2025/11/logo_nai_branco.png"
            alt="NAI"
            className="h-9 w-auto"
          />
          <div className="border-l border-slate-700 pl-3 flex-1">
            <div className="text-xs font-bold uppercase tracking-widest text-cyan-400">
              Nai Compliance Center
            </div>
            <div className="text-sm font-semibold text-white">
              Assessments de Maturidade
            </div>
          </div>
          <a
            href="https://wa.me/5511996344065?text=Ol%C3%A1%21%20Gostaria%20de%20conversar%20sobre%20os%20assessments%20de%20maturidade."
            target="_blank"
            rel="noopener"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition"
          >
            Falar com especialista
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 pt-16 pb-12 sm:pt-24 sm:pb-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/40 text-cyan-400 text-[11px] font-bold uppercase tracking-widest mb-6">
            ✱ 5 pilares · 1 framework
          </div>
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight leading-[1.05]">
            Onde está sua organização em{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              governança, risco e compliance
            </span>
            ?
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Escolha o pilar e responda em até 15 minutos. Receba um relatório
            instantâneo com score consolidado, gráfico radar dos domínios e
            roadmap de prioridades alinhado à sua realidade.
          </p>

          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-slate-500 flex-wrap">
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Sem login
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
              Resultado imediato
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
              PDF para download
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              Dados criptografados
            </span>
          </div>
        </div>
      </section>

      {/* Pillar cards */}
      <section className="px-4 pb-20">
        <div className="max-w-6xl mx-auto grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((p) => <PillarCardComponent key={p.id} pillar={p} />)}
        </div>
      </section>

      {/* Why */}
      <section className="px-4 pb-20">
        <div className="max-w-5xl mx-auto bg-slate-900/60 border border-slate-800 rounded-2xl p-8 sm:p-12">
          <div className="text-xs font-bold uppercase tracking-widest text-cyan-400 mb-3">
            Por que um assessment NAI?
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-6">
            Diagnóstico não é checklist. É leitura de risco real.
          </h2>
          <div className="grid sm:grid-cols-3 gap-6 text-sm text-slate-400">
            <div>
              <div className="text-3xl font-black text-cyan-400 tracking-tight">100%</div>
              <p className="mt-1 text-slate-300 font-semibold">Brasileiro</p>
              <p className="mt-2 leading-relaxed">
                22 anos de pedigree em GRC · sede Campinas-SP · time técnico nacional.
              </p>
            </div>
            <div>
              <div className="text-3xl font-black text-emerald-400 tracking-tight">5</div>
              <p className="mt-1 text-slate-300 font-semibold">Pilares integrados</p>
              <p className="mt-2 leading-relaxed">
                IGA + Privacy + Cadeia + ESG + Cibersec sob a mesma metodologia e plataforma.
              </p>
            </div>
            <div>
              <div className="text-3xl font-black text-violet-400 tracking-tight">∞</div>
              <p className="mt-1 text-slate-300 font-semibold">Reproduzível</p>
              <p className="mt-2 leading-relaxed">
                Aplique nos seus times, fornecedores, BU's — compare maturidades ao longo do tempo.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="px-4 py-8 border-t border-slate-800 text-center text-[11px] text-slate-500">
        © NAI · Nai Compliance Center · nai-it.com · 100% brasileira desde 2004
      </footer>
    </div>
  )
}

function PillarCardComponent({ pillar }: { pillar: PillarCard }) {
  const isActive = pillar.status === 'active'

  const inner = (
    <div className={`group relative h-full flex flex-col bg-slate-900/60 border rounded-2xl p-6 transition-all duration-300 ${
      isActive
        ? 'border-slate-700 hover:border-cyan-500 hover:shadow-2xl hover:shadow-cyan-500/10 hover:-translate-y-1 cursor-pointer'
        : 'border-slate-800 opacity-70'
    }`}>
      {/* Gradient top bar */}
      <div
        className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
        style={{ background: `linear-gradient(90deg, ${pillar.accentFrom}, ${pillar.accentTo})` }}
      />

      {/* Icon */}
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center text-white mb-4"
        style={{ background: `linear-gradient(135deg, ${pillar.accentFrom}, ${pillar.accentTo})` }}
      >
        <div className="w-6 h-6">{pillar.icon}</div>
      </div>

      {/* Status badge */}
      <div className="absolute top-4 right-4">
        {isActive ? (
          <span className="px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 text-[10px] font-bold uppercase tracking-wider">
            ● Ativo
          </span>
        ) : (
          <span className="px-2.5 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
            {pillar.comingSoonAt || 'Em breve'}
          </span>
        )}
      </div>

      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">
        {pillar.tagline}
      </div>
      <h3 className="text-lg font-bold text-white leading-tight tracking-tight">
        {pillar.title}
      </h3>
      <p className="mt-2.5 text-sm text-slate-400 leading-relaxed flex-1">
        {pillar.description}
      </p>

      <ul className="mt-4 space-y-1.5">
        {pillar.bullets.map((b, i) => (
          <li key={i} className="text-xs text-slate-400 flex items-start gap-2">
            <span className="mt-0.5 font-bold" style={{ color: pillar.accentSolid }}>→</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {/* Stats row */}
      <div className="mt-5 pt-4 border-t border-slate-800 flex items-center justify-between text-[11px] text-slate-500">
        <span>{pillar.domainCount} domínios · {pillar.questionCount} perguntas</span>
        <span className="font-mono">~{pillar.duration}</span>
      </div>

      {/* CTA */}
      <div className="mt-4">
        {isActive ? (
          <span
            className="inline-flex items-center justify-center w-full gap-2 px-4 py-2.5 rounded-full text-white font-bold text-sm group-hover:scale-[1.02] transition shadow-lg"
            style={{ background: `linear-gradient(90deg, ${pillar.accentFrom}, ${pillar.accentTo})` }}
          >
            Iniciar diagnóstico →
          </span>
        ) : (
          <button
            disabled
            className="w-full px-4 py-2.5 rounded-full bg-slate-800 text-slate-500 font-bold text-sm cursor-not-allowed"
          >
            Em desenvolvimento
          </button>
        )}
      </div>
    </div>
  )

  if (isActive && pillar.href) {
    return <Link to={pillar.href} className="block h-full">{inner}</Link>
  }
  return inner
}
